import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../../store/authStore'
import { useToastStore } from '../../../store/toastStore'
import { useOnboardingCelebration } from '../../../store/onboardingCelebrationStore'
import { useOnboardingGuidedStore } from '../../../store/onboardingGuidedStore'
import {
  isOnboardingGuideCoreComplete,
  isOnboardingGuideFinished,
  isRecentOnboardingRewardGrant,
} from '../../../lib/onboarding/onboardingGuideConfig'
import {
  hasOnboardingCelebrationBeenShown,
  markOnboardingCelebrationShown,
  markOnboardingCompletedAt,
} from '../../../lib/onboarding/onboardingRoiCardState'
import { useInvalidateCreditBalance } from '../../credits/useCreditBalance'
import { useOnboardingStatus } from './useOnboarding'

/** Toast, modal i sync bonusu po ukończeniu przewodnika. */
export function useOnboardingCompletionEffects() {
  const { t } = useTranslation('dashboard')
  const userId = useAuthStore((s) => s.user?.id)
  const { data, refetch } = useOnboardingStatus()
  const triggerCelebration = useOnboardingCelebration((s) => s.trigger)
  const addToast = useToastStore((s) => s.addToast)
  const stopGuided = useOnboardingGuidedStore((s) => s.stop)
  const invalidateCreditBalance = useInvalidateCreditBalance()
  const syncAttemptedRef = useRef(false)

  const completedKeys = useMemo(
    () =>
      new Set(
        (data?.steps ?? [])
          .filter((s) => s.completed)
          .map((s) => s.key),
      ),
    [data?.steps],
  )

  const guideCoreComplete = isOnboardingGuideCoreComplete(completedKeys)
  const guideFinished = isOnboardingGuideFinished(data, completedKeys)

  useEffect(() => {
    if (!guideFinished) return
    stopGuided()
  }, [guideFinished, stopGuided])

  useEffect(() => {
    if (!guideCoreComplete || syncAttemptedRef.current) return
    // Core done but bonus not on ledger yet (auto-detect path) — refetch GET /status
    // which reconciles rewards, then refresh the balance chip.
    if (data?.reward_granted) return
    syncAttemptedRef.current = true
    void refetch().then(() => invalidateCreditBalance())
  }, [
    guideCoreComplete,
    data?.reward_granted,
    refetch,
    invalidateCreditBalance,
  ])

  useEffect(() => {
    if (!userId || !data?.reward_granted) return
    if (!data.all_completed && !guideCoreComplete) return
    if (!isRecentOnboardingRewardGrant(data.reward_granted_at)) return
    if (hasOnboardingCelebrationBeenShown(userId)) return

    markOnboardingCelebrationShown(userId)
    markOnboardingCompletedAt()

    const pointsAdded = data.completion_bonus
    triggerCelebration(pointsAdded, data.completion_bonus)
    addToast(t('notifications.onboardingComplete.title'), 'success')
    invalidateCreditBalance()
  }, [
    userId,
    data?.all_completed,
    data?.reward_granted,
    data?.reward_granted_at,
    data?.completion_bonus,
    data?.reward_points,
    guideCoreComplete,
    triggerCelebration,
    addToast,
    t,
    invalidateCreditBalance,
  ])
}
